import { useEffect, useMemo, useState } from 'react'
import useSWRInfinite from 'swr/infinite'
import { Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { faFolder } from '@fortawesome/pro-solid-svg-icons'
import { Input } from 'antd'
import { useParams } from 'react-router-dom'
import classNames from 'classnames'
import EmptyListMessage from 'components/ui/empty-list-message/EmptyListMessage'
import { useAccess } from 'contexts/AccessContext'
import { CourseVerticalCard } from 'components/ui/CourseCards'
import ViewAllCourses from 'api/courses/ViewAll'
import Loader from 'components/ui/loader/Loader'
import { useElementVisible } from 'hooks/useScrollFetch'
import { courseSearch } from 'helpers/courseSearch'
import { useIsMobile } from 'hooks/useIsMobile'
import { FolderHeader } from '../my-courses/atoms/FolderHeader'
import { GoBackButton } from '../Buttons/GoBackButton'
import { COURSE_PROGRESS, ICONS, KEYS_TITLE_TRANSLATIONS } from './constants'
import { FilterTypes } from './enums'
import { QueryParams, UrlParams } from './types'
import styles from './ViewAll.module.scss'

export const ViewAll = () => {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState(search)
  const { roles } = useAccess()
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const params = useParams<UrlParams>()

  const scope = params.scope

  const isLibrary = scope === 'library'

  const { data, isLoading, setSize, isValidating, size } = useSWRInfinite(
    (index: number) => {
      return {
        key: `${index}/view-all/${scope}/${params.filter_by}/${params.folder_id}`,
        page: index + 1,
      }
    },
    async ({ page }) => ViewAllCourses(buildQueryBody(page))
  )

  const lastData = data && data[data.length - 1]
  // The last data has the last total
  const total = lastData?.total || 0

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined')
  const loading = isLoading || isLoadingMore
  const firstLoad = loading && !data

  const { elementRef } = useElementVisible({
    onVisible: async () => {
      if (loading || isValidating) return

      if (lastData) {
        if (!lastData.courses.length) return
      }
      setSize((size) => size + 1)
    },
  })

  const buildQueryBody = (page: number) => {
    const queryParams: QueryParams = { page: page }
    if (params.filter_by === 'folder' && params.folder_id) {
      queryParams.folder = params.folder_id
    } else if (params.filter_by === 'premieres') {
      queryParams.premieres = true
    } else if (params.filter_by === 'popular') {
      queryParams.populars = true
    } else if (params.filter_by === 'closed') {
      queryParams.state = 3
    } else if (params.filter_by === 'upcoming') {
      queryParams.state = 4
    } else if (params.filter_by in COURSE_PROGRESS) {
      queryParams.advance = COURSE_PROGRESS[params.filter_by]
    }

    if (scope === 'my-courses') {
      queryParams.scope = 'my_courses'
    } else if (scope === 'library') {
      queryParams.scope = 'library'
    } else if (scope === 'catalogue') {
      queryParams.scope = 'all'
    }

    return queryParams
  }

  const filter_by = params.filter_by as FilterTypes

  const title = useMemo(() => {
    const keyTranslation = KEYS_TITLE_TRANSLATIONS[filter_by]
    if (filter_by === FilterTypes.folder) {
      const title = params.folder_title
      if (title) return title
    }
    return t(`viewAll.title.${keyTranslation}`) || ''
  }, [filter_by, params.folder_title, t])

  const { icon, color: iconColor } = useMemo(
    () => ICONS[filter_by] || { icon: faFolder, color: '#4C4C4C' },
    [filter_by]
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 1000)
    return () => clearTimeout(timer)
  }, [search])

  const dataFiltered = data
    ?.flatMap(({ courses }) => courses)
    .filter((c) => courseSearch(debouncedSearch, c.course.name))

  return (
    <Row
      className={classNames(
        styles.viewAllWrapper,
        isMobile ? styles.wrapperMobile : ''
      )}
    >
      <GoBackButton />
      <Col
        lg={{ span: 4, offset: 6 }}
        className={`mb-4 ${styles.searchWrapper}`}
      >
        <Input.Search
          placeholder={t('home.lmsIndex.search.placeholder')}
          className={`${styles.search}`}
          value={search}
          onChange={(e: any) => setSearch(e.target.value)}
        />
      </Col>
      <Col lg={{ span: 12 }} className={styles.folderHeaderWrapper}>
        <FolderHeader
          iconColor={iconColor}
          icon={icon}
          title={title}
          count={total}
          viewAll={false}
        />
      </Col>
      {data?.flat().length === 0 && !isLoading && (
        <div className={styles.emptyListWrapper}>
          <EmptyListMessage>{t('viewAll.emptyComponent')}</EmptyListMessage>
        </div>
      )}
      <div className={isMobile ? styles.mobileWrapper : ''}>
        <div className={styles.allCardsWrapper}>
          {!firstLoad &&
            dataFiltered?.map(({ course, enrol }) => (
              <div key={course.id || ''}>
                <div
                  style={{
                    marginRight: '24px',
                  }}
                >
                  <CourseVerticalCard
                    course={course}
                    enrol={enrol}
                    enrolled={
                      !roles.superadmin &&
                      // When is library, the user is not enrolled
                      !isLibrary
                    }
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
      <div ref={elementRef} />
      {(isLoading || isLoadingMore) && (
        <div className={styles.loaderWrapper}>
          <Loader color />
        </div>
      )}
    </Row>
  )
}

// El componente se renderiza con este router:
// En el componetne <ShowMore> recibe como prop todos los parámetros que van después de 'view-all/' en la url
//        <Route path='/view-all/:scope/:filter_by/:folder_id?/:folder_title?' component={ViewAll} />
// Recibe los siguientes parámetros:
// 1.- :scope:     Obligatorio esto toma el valor de “my_courses” cuando entro desde mis cursos, y  “catalogue”  cuando entro
//                      desde el inicio. Esto filtrará si son cursos generales o del usuario.

// 2.- :filter_by:      Obligatorio . Toma valores de: pending, in_progress, upcoming, closed, premieres finished, folder.

//  Si se filtra por “folder” , se agregan  los parametros

//  3.- :folder_id:      que es el número del id de la carpeta

//  4.- :folder_title:   que es el título/nombre de la carpeta, con esto como parámetro en la url nos evitamos tener que traernos ese dato desde la api y permite que el método sea más genérico.
